import { useState } from "react";
import { Phone, Mail, MapPin, Clock, ArrowUpRight } from "lucide-react";
import { toast } from "sonner";
import PageHero from "@/components/PageHero";
import { FadeIn } from "@/components/FadeIn";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { CONTACT_INFO, FEATURED_CENTER } from "@/lib/constants";
import { submitEnquiry } from "@/lib/api";

const INFO_CARDS = [
  { icon: Phone, title: "Call Us", lines: [FEATURED_CENTER.phone], href: `tel:${FEATURED_CENTER.phone.replace(/\s/g, "")}` },
  { icon: Mail, title: "Email Us", lines: [CONTACT_INFO.email], href: `mailto:${CONTACT_INFO.email}` },
  { icon: MapPin, title: "Visit Us", lines: ["Sairam Central Mall, Manipal,", "Udupi, Karnataka 576104"], href: FEATURED_CENTER.maps },
  { icon: Clock, title: "Working Hours", lines: [CONTACT_INFO.hours] },
];

const inputCls =
  "mt-2 bg-transparent border-x-0 border-t-0 border-b border-[#050505]/30 rounded-none focus-visible:ring-0 focus-visible:border-[#c9a14a] px-0 font-light";

export default function Contact() {
  const [submitting, setSubmitting] = useState(false);
  const [form, setForm] = useState({ name: "", phone: "", email: "", city: "", message: "" });
  const update = (k) => (e) => setForm({ ...form, [k]: e.target.value });

  const submit = async (e) => {
    e.preventDefault();
    setSubmitting(true);
    try {
      await submitEnquiry({ type: "contact", ...form, source_page: "/contact" });
      toast.success("Message sent", { description: "Our team will get back to you within 24 hours." });
      setForm({ name: "", phone: "", email: "", city: "", message: "" });
    } catch {
      toast.error("Something went wrong", { description: "Please try again or call us directly." });
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <main data-testid="contact-page" className="bg-[#f7f2e8]">
      <PageHero
        crumb="Contact Us"
        line1="We're Here"
        line2="to Listen."
        subtitle="Questions about programs, memberships or visiting a center — reach out and a Carlton specialist will respond within 24 hours."
      />

      <section className="max-w-[1480px] mx-auto px-5 md:px-10 py-20 md:py-24">
        <div className="grid grid-cols-1 sm:grid-cols-2 xl:grid-cols-4 gap-5">
          {INFO_CARDS.map(({ icon: Icon, title, lines, href }, i) => (
            <FadeIn key={title} delay={i * 0.07} className="bg-white border border-[#c9a14a]/20 p-8" data-testid={`contact-info-${title.toLowerCase().replace(/\s+/g, "-")}`}>
              <span className="w-12 h-12 rounded-full border border-[#c9a14a]/50 flex items-center justify-center text-[#a8813a]">
                <Icon size={19} strokeWidth={1.5} />
              </span>
              <p className="mt-5 font-semibold text-[#141414]">{title}</p>
              <div className="mt-2 space-y-1">
                {lines.map((l) =>
                  href ? (
                    <a key={l} href={href} target={href.startsWith("http") ? "_blank" : undefined} rel="noopener noreferrer" className="block text-[13px] text-[#4a4a4a] hover:text-[#a8813a] transition-colors">
                      {l}
                    </a>
                  ) : (
                    <p key={l} className="text-[13px] text-[#4a4a4a]">{l}</p>
                  )
                )}
              </div>
            </FadeIn>
          ))}
        </div>

        <div className="mt-16 grid lg:grid-cols-2 gap-12 items-start">
          <FadeIn>
            <p className="overline text-[#c9a14a]">Send a Message</p>
            <h2 className="mt-4 font-serif-display text-3xl sm:text-4xl text-[#141414]">
              Tell us how we <span className="text-[#c9a14a]">can help.</span>
            </h2>
            <p className="mt-5 text-[#4a4a4a] text-base leading-relaxed max-w-md">
              Every message is read by a real person on our team. Share your goal, question
              or feedback and we'll take it from there.
            </p>
            <div className="mt-8 rounded-2xl overflow-hidden shadow-xl">
              <img src="/assets/gallery/02.jpg" alt="Carlton Wellness Center" className="w-full aspect-[16/9] object-cover" />
            </div>
          </FadeIn>

          <FadeIn delay={0.12} className="bg-white border border-[#c9a14a]/20 p-8 md:p-10">
            <form onSubmit={submit} className="space-y-5" data-testid="contact-form">
              <div className="grid sm:grid-cols-2 gap-5">
                <div>
                  <Label className="overline text-[#050505]/70 text-[10px]" htmlFor="c-name">Name</Label>
                  <Input id="c-name" data-testid="contact-name-input" required value={form.name} onChange={update("name")} className={inputCls} />
                </div>
                <div>
                  <Label className="overline text-[#050505]/70 text-[10px]" htmlFor="c-phone">Phone</Label>
                  <Input id="c-phone" type="tel" data-testid="contact-phone-input" required value={form.phone} onChange={update("phone")} className={inputCls} />
                </div>
              </div>
              <div>
                <Label className="overline text-[#050505]/70 text-[10px]" htmlFor="c-email">Email</Label>
                <Input id="c-email" type="email" data-testid="contact-email-input" required value={form.email} onChange={update("email")} className={inputCls} />
              </div>
              <div>
                <Label className="overline text-[#050505]/70 text-[10px]" htmlFor="c-city">City</Label>
                <Input id="c-city" data-testid="contact-city-input" required value={form.city} onChange={update("city")} className={inputCls} />
              </div>
              <div>
                <Label className="overline text-[#050505]/70 text-[10px]" htmlFor="c-message">Your Message</Label>
                <Textarea
                  id="c-message"
                  data-testid="contact-message-input"
                  required
                  value={form.message}
                  onChange={update("message")}
                  rows={4}
                  className="mt-2 bg-transparent border border-[#050505]/20 rounded-none focus-visible:ring-0 focus-visible:border-[#c9a14a] font-light"
                />
              </div>
              <button type="submit" data-testid="contact-submit-button" disabled={submitting} className="btn-gold w-full justify-center disabled:opacity-60">
                {submitting ? "Sending…" : "Send Message"}
                {!submitting && <ArrowUpRight size={16} strokeWidth={1.5} />}
              </button>
            </form>
          </FadeIn>
        </div>
      </section>
    </main>
  );
}
